/**
 * mediaService
 * Firebase Storage upload/delete helpers for dashboard-managed media.
 *
 * Path conventions:
 * - content media: `{collectionKey}/{documentId}/{folder}/...`
 * - user utility uploads: `users/{userId}/{folder}/...`
 */
import {
  deleteObject,
  getDownloadURL,
  ref,
  uploadBytesResumable,
} from 'firebase/storage';
import { assertFirebaseConfigured, storage } from '../lib/firebase/config';

function sanitizeFileName(fileName) {
  return fileName.replace(/[^a-zA-Z0-9.-]/g, '-');
}

function uploadFile(path, file, onProgress) {
  return new Promise((resolve, reject) => {
    const storageRef = ref(storage, path);
    const uploadTask = uploadBytesResumable(storageRef, file);

    uploadTask.on(
      'state_changed',
      (snapshot) => {
        if (!onProgress) {
          return;
        }
        const progress = Math.round(
          (snapshot.bytesTransferred / snapshot.totalBytes) * 100
        );
        onProgress(progress);
      },
      reject,
      async () => {
        // Persisted to Firestore by callers as `...Url` + `...Path`.
        const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
        resolve({
          downloadURL,
          fullPath: uploadTask.snapshot.ref.fullPath,
          contentType: file.type || '',
          size: file.size,
          name: file.name,
        });
      }
    );
  });
}

export async function uploadContentFile({
  collectionKey,
  documentId,
  folder = 'assets',
  file,
  onProgress,
}) {
  assertFirebaseConfigured();
  const safeFileName = sanitizeFileName(file.name);
  // Timestamp prefix avoids collisions for repeated uploads of same filename.
  const path = `${collectionKey}/${documentId}/${folder}/${Date.now()}-${safeFileName}`;
  return uploadFile(path, file, onProgress);
}

export async function uploadFileToUserFolder({ userId, folder, file, onProgress }) {
  assertFirebaseConfigured();
  const safeFileName = sanitizeFileName(file.name);
  const path = `users/${userId}/${folder}/${Date.now()}-${safeFileName}`;
  return uploadFile(path, file, onProgress);
}

export async function deleteStoragePath(path) {
  assertFirebaseConfigured();
  if (!path) {
    return;
  }

  const storageRef = ref(storage, path);
  try {
    await deleteObject(storageRef);
  } catch (error) {
    // Delete is idempotent from UI perspective.
    if (error.code !== 'storage/object-not-found') {
      throw error;
    }
  }
}
